import { ServiceResponse } from "@rsm-hcd/javascript-core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { QueryKey, useQueryClient } from "react-query";
import { BlockConfigurationRecord } from "models/view-models/block-configurations/block-configuration-record";
import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import ChecklistBlockConfigurationChecklistItemService from "utilities/services/block-configurations/checklistitems/checklist-block-configuration-checklist-items-service";
import { CollectionUtils } from "utilities/collection-utils";
import PublicationService, {
    PublicationListQueryParams,
} from "utilities/services/publications/publication-service";
import PublicPublicationService from "utilities/services/public/publications/publication-service";
import PublicationRecord from "models/view-models/publication-record";
import { useSortChecklistItems } from "utilities/hooks/domain/block-configurations/use-sort-checklist-items";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";
import SharedChecklistBlockConfigurationChecklistItemService from "utilities/services/shared-checklist-items/shared-checklist-block-configuration-checklist-items-service";
import ServiceResponseFactory from "utilities/services/service-response-factory";

interface UseChecklistBlockChecklistItems {
    errorMessages: string[];
    isLoadingItems: boolean;
    items: ChecklistItemRecord[];
    onItemsReordered: (
        updatedItems: ChecklistItemRecord[]
    ) => Promise<number[]>;
    updateItems: (items: ChecklistItemRecord[]) => void;
}

const DEFAULT_ERROR_MESSAGE: string =
    "Could not fetch publications for referenced sections";
const ERROR_MESSAGE_DELIMITER: string = ";";
const CHECKLIST_ITEMS_STALE_TIME = 0;
const EMPTY_CHECKLIST_ITEMS: ChecklistItemRecord[] = [];

export const useChecklistBlockChecklistItems = (
    blockConfiguration: BlockConfigurationRecord
): UseChecklistBlockChecklistItems => {
    const { pageGuid, recipientGuid } = useSharedPageContext();
    const queryClient = useQueryClient();
    const publicationService = pageGuid
        ? PublicPublicationService
        : PublicationService;
    const { list: listPublicationsApi } = publicationService.useList();
    const [publications, setPublications] = useState<PublicationRecord[]>([]);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const { list: listItemsQuery } =
        ChecklistBlockConfigurationChecklistItemService.useListQuery();
    const { list: listSharedItemsQuery } =
        SharedChecklistBlockConfigurationChecklistItemService.useListQuery();
    const hasValidBlockConfiguration =
        blockConfiguration?.id != null && blockConfiguration.id > 0;
    const itemsQuery = listItemsQuery(
        { configurationId: blockConfiguration?.id ?? 0 },
        undefined,
        {
            enabled: hasValidBlockConfiguration && !pageGuid,
            staleTime: CHECKLIST_ITEMS_STALE_TIME,
        }
    );
    const sharedItemsQuery = listSharedItemsQuery(
        {
            pageGuid: pageGuid ?? "",
            configurationId: blockConfiguration?.id ?? 0,
        },
        { recipient: recipientGuid },
        {
            enabled: hasValidBlockConfiguration && pageGuid != null,
            staleTime: CHECKLIST_ITEMS_STALE_TIME,
        }
    );
    const checklistItemsQuery = pageGuid ? sharedItemsQuery : itemsQuery;
    const queryItems =
        checklistItemsQuery.data?.resultObjects ?? EMPTY_CHECKLIST_ITEMS;

    const attachPublicationsToItems = useCallback(
        (
            items: ChecklistItemRecord[],
            publications: PublicationRecord[]
        ): ChecklistItemRecord[] =>
            items.map((ci) => {
                if (
                    CollectionUtils.isEmpty(ci.note?.checklistItemNoteSections)
                ) {
                    return ci;
                }

                const checklistItemNoteSections =
                    ci.note.checklistItemNoteSections;
                const checklistItemNoteSectionsWithPublications =
                    checklistItemNoteSections.map((cins) => {
                        if (cins.section?.publicationId == null) {
                            return cins;
                        }

                        const publication = publications.find(
                            (p) => p.id === cins.section!.publicationId
                        );

                        if (publication == null) {
                            return cins;
                        }

                        const updatedSection = cins.section.with({
                            publication: publication,
                        });

                        return cins.with({ section: updatedSection });
                    });

                const updatedNote = ci.note.with({
                    checklistItemNoteSections:
                        checklistItemNoteSectionsWithPublications,
                });

                return ci.with({ note: updatedNote });
            }),
        []
    );

    const getDistinctPublicationIds = useCallback(
        (items: ChecklistItemRecord[]): number[] => {
            const checklistItemNoteSections = items.flatMap(
                (ci) => ci?.note?.checklistItemNoteSections
            );
            const publicationIds = checklistItemNoteSections
                .filter((cins) => cins?.section?.publicationId != null)
                .map((cins) => cins!.section!.publicationId!);

            return Array.from(new Set(publicationIds));
        },
        []
    );

    const loadPublications = useCallback(
        async (
            publicationIds: number[]
        ): Promise<PublicationRecord[] | null> => {
            if (CollectionUtils.isEmpty(publicationIds)) {
                return [];
            }

            try {
                const queryParams: PublicationListQueryParams = {
                    publicationIds: publicationIds,
                };

                const response = await listPublicationsApi(queryParams);

                if (
                    response?.resultObjects == null ||
                    response.results == null
                ) {
                    throw new Error(DEFAULT_ERROR_MESSAGE);
                }

                if (response.results.hasErrors()) {
                    throw new Error(
                        response.results
                            .listErrorMessages()
                            .join(ERROR_MESSAGE_DELIMITER)
                    );
                }

                return response.resultObjects;
            } catch (error) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : DEFAULT_ERROR_MESSAGE;

                setErrorMessages(errorMessage.split(ERROR_MESSAGE_DELIMITER));

                return null;
            }
        },
        [listPublicationsApi]
    );

    const loadPublicationsForItems = useCallback(
        async (items: ChecklistItemRecord[]): Promise<void> => {
            if (CollectionUtils.isEmpty(items)) {
                setPublications([]);
                return;
            }

            const publicationIds = getDistinctPublicationIds(items);
            setPublications((await loadPublications(publicationIds)) ?? []);
        },
        [getDistinctPublicationIds, loadPublications]
    );

    const items = useMemo(() => {
        if (CollectionUtils.isEmpty(publications)) {
            return queryItems;
        }

        return attachPublicationsToItems(queryItems, publications);
    }, [attachPublicationsToItems, publications, queryItems]);
    const { sortedItems, onItemsReordered } = useSortChecklistItems(items);

    const getChecklistItemsQueryKey = useCallback((): QueryKey => {
        const routeParams = pageGuid
            ? {
                  pageGuid: pageGuid ?? "",
                  configurationId: blockConfiguration?.id ?? 0,
              }
            : { configurationId: blockConfiguration?.id ?? 0 };
        const queryParams = pageGuid ? { recipient: recipientGuid } : undefined;

        return [
            pageGuid
                ? SharedChecklistBlockConfigurationChecklistItemService.key
                : ChecklistBlockConfigurationChecklistItemService.key,
            { routeParams, queryParams },
        ];
    }, [blockConfiguration?.id, pageGuid, recipientGuid]);

    const updateItems = useCallback(
        (newItems: ChecklistItemRecord[]): void => {
            queryClient.setQueryData<
                ServiceResponse<ChecklistItemRecord> | undefined
            >(getChecklistItemsQueryKey(), (previousResponse) => {
                if (previousResponse == null) {
                    return ServiceResponseFactory.successfulList(
                        newItems,
                        newItems.length
                    );
                }

                return {
                    ...previousResponse,
                    resultObjects: newItems,
                };
            });
        },
        [getChecklistItemsQueryKey, queryClient]
    );

    useEffect(() => {
        if (checklistItemsQuery.isLoading || checklistItemsQuery.isIdle) {
            return;
        }

        if (checklistItemsQuery.isError) {
            const errorMessage =
                checklistItemsQuery.error instanceof Error
                    ? checklistItemsQuery.error.message
                    : DEFAULT_ERROR_MESSAGE;
            setErrorMessages(errorMessage.split(ERROR_MESSAGE_DELIMITER));
            return;
        }

        const response = checklistItemsQuery.data;
        if (response?.resultObjects == null) {
            setErrorMessages(["No response received"]);
            return;
        }

        if (response.results?.hasErrors()) {
            setErrorMessages(
                response.results
                    .listErrorMessages()
                    .join(ERROR_MESSAGE_DELIMITER)
                    .split(ERROR_MESSAGE_DELIMITER)
            );
            return;
        }

        void loadPublicationsForItems(response.resultObjects);
    }, [
        checklistItemsQuery.data,
        checklistItemsQuery.error,
        checklistItemsQuery.isError,
        checklistItemsQuery.isIdle,
        checklistItemsQuery.isLoading,
        loadPublicationsForItems,
    ]);

    // This is to account for the period where the query is loaded but the items havne't been sorted yet
    const isSyncingSortedItems =
        checklistItemsQuery.data?.resultObjects != null &&
        sortedItems.length !== items.length;

    return {
        errorMessages: errorMessages,
        isLoadingItems:
            checklistItemsQuery.isFetching ||
            checklistItemsQuery.isLoading ||
            checklistItemsQuery.isIdle ||
            isSyncingSortedItems,
        items: sortedItems,
        onItemsReordered: onItemsReordered,
        updateItems,
    };
};
